import { CopyOutlined } from '@ant-design/icons'
import { Alert, App, Button, Descriptions, Modal, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { TenantProvisionResult } from './tenantsApi'

interface Props {
  result: TenantProvisionResult | null
  onClose: () => void
}

/**
 * Yangi tashkilotning birinchi admin hisobi — <b>bir martalik</b>
 * ko'rsatiladigan oyna.
 *
 * <h2>⚠️ Parol BOSHQA HECH QAYERDA yo'q</h2>
 * Server uni faqat yaratish javobida qaytaradi:
 * <ul>
 *   <li>bazada faqat BCrypt hash;</li>
 *   <li>audit yozuviga tushmaydi (audit `audit.read` borlar uchun ochiq);</li>
 *   <li>server logiga yozilmaydi;</li>
 *   <li>panel uni `localStorage` ga ham, TanStack Query keshiga ham
 *       yozmaydi — u faqat `useState` da, oyna yopilishi bilan
 *       yo'qoladi.</li>
 * </ul>
 *
 * <p>Shuning uchun oyna ATAYLAB "X" tugmasi bilan tasodifan yopilmaydi
 * (`closable={false}`, `maskClosable={false}`): yopish uchun ochiq
 * tasdiq kerak. Yo'qolgan taqdirda yagona yo'l —
 * `PUT /api/users/{id}/password`.
 */
export function TenantCredentialsModal({ result, onClose }: Props) {
  const { t } = useTranslation()
  const { message } = App.useApp()

  if (!result) return null

  const copy = (value: string) => {
    // `navigator.clipboard` HTTPS'siz kontekstda mavjud emas — panel
    // HTTPS ostida ishlaydi, lekin lokal `http://ip:port` da ochilsa
    // yo'q. Shu sabab tekshiriladi va xato JIM qolmaydi.
    if (!navigator.clipboard) {
      void message.error(t('tenants.copyUnavailable'))
      return
    }
    void navigator.clipboard.writeText(value).then(
      () => void message.success(t('tenants.copied')),
      () => void message.error(t('tenants.copyUnavailable')),
    )
  }

  return (
    <Modal
      open
      title={t('tenants.credentialsTitle')}
      closable={false}
      maskClosable={false}
      keyboard={false}
      onOk={onClose}
      okText={t('tenants.credentialsAck')}
      cancelButtonProps={{ style: { display: 'none' } }}
      width={620}
    >
      <Alert
        type="success"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('tenants.created', {
          code: result.tenant.code,
          roles: result.rolesCreated,
        })}
      />
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('tenants.passwordOnceWarning')}
      />
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('tenants.code')}>
          <Typography.Text code>{result.tenant.code}</Typography.Text>
        </Descriptions.Item>
        {/*
          ⚠️ Realm ixtiyoriy (`V202608062800`). Bo'lmasa nusxa olish
          tugmasi ham KO'RSATILMAYDI: bo'sh qiymatni nusxalash operatorga
          "bir narsa bor edi, olmadim" degan taassurot qoldirardi.
        */}
        <Descriptions.Item label={t('tenants.realm')}>
          {result.tenant.realm ? (
            <Space>
              <Typography.Text code>{result.tenant.realm}</Typography.Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copy(result.tenant.realm as string)}
              />
            </Space>
          ) : (
            <Typography.Text type="secondary">{t('tenants.realmNone')}</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('tenants.adminUsername')}>
          <Space>
            <Typography.Text code>{result.adminUsername}</Typography.Text>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copy(result.adminUsername)}
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('tenants.adminPassword')}>
          {result.adminPassword ? (
            <Space>
              <Typography.Text code style={{ fontSize: 16 }}>
                {result.adminPassword}
              </Typography.Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copy(result.adminPassword as string)}
              />
            </Space>
          ) : (
            // Operator parolni o'zi kiritgan — serverdan qaytmaydi va
            // uni bu yerda takrorlash keraksiz tarqatish bo'lardi.
            <Typography.Text type="secondary">{t('tenants.passwordOwn')}</Typography.Text>
          )}
        </Descriptions.Item>
      </Descriptions>
      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
        {t('tenants.nextSteps')}
      </Typography.Paragraph>
    </Modal>
  )
}
